/**
 * Theme (light / dark / system) state.
 *
 * Wraps VueUse `useColorMode`, which applies the resolved scheme as a
 * `data-theme` attribute on `<html>` (matching the design-token file's
 * `:root[data-theme="dark"]` selector — #223), persists the choice to
 * localStorage, and — when set to `auto` — follows the OS `prefers-color-scheme`
 * and reacts live to OS changes. Defined as a module-level singleton so every
 * caller shares one source of truth.
 */
import { useColorMode } from '@vueuse/core'

export type ThemeMode = 'light' | 'dark' | 'auto'

// emitAuto keeps `mode` as the user's *choice* ('auto'/'light'/'dark') while the
// applied <html data-theme> reflects the resolved scheme (system value when
// 'auto'). The token file keys dark off `data-theme="dark"`, so we drive that
// attribute rather than a `.dark` class.
const mode = useColorMode({ attribute: 'data-theme', emitAuto: true })

export function useTheme() {
  function setMode(next: ThemeMode) {
    mode.value = next
  }

  return { mode, setMode }
}
